// pages/dashboard/views/PromoCodesView.jsx
// NEW — admin UI for the celebrity/influencer promo-code bonus feature
// (modules/promotions on the server). Structurally mirrors
// CouponsView.jsx's modal-based CRUD pattern (same codebase convention),
// adapted for promo codes: no delete action (redemptions reference a
// code permanently for audit purposes — disable it instead via the
// isActive toggle), plus a stats panel showing the locked/unlocked
// redemption breakdown the user asked for ("gate it properly... so
// the platforms lose" — this is the visibility half of that gating).
//
// EXTENDED (Ref/Promo code merge) — a code can now be "General" (the
// original behavior: anyone can redeem it, no owner) or "Assigned to a
// user": admin picks an existing user via the search box below and sets
// a bonusAmount just for that code. Redeeming an assigned code also
// links the redeemer into the owning user's downline for the existing
// deposit-commission system (see modules/identity/controllers/
// referralSystem.js) — this is in addition to, not instead of, that
// user's own free auto-generated referral code. Like `code` and
// `bonusAmount`, type/assignedToUserId are only settable at creation —
// see the matching disabled-on-edit note on those fields.
import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient, getAdminUsers } from "../../../api/apiServices";

function fmt(n) { return Number(n || 0).toLocaleString("en-NG"); }

function Badge({ label, color = "slate" }) {
  const map = { emerald: "bg-emerald-100 text-emerald-700", blue: "bg-blue-100 text-blue-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-500", slate: "bg-slate-100 text-slate-500", purple: "bg-purple-100 text-purple-700" };
  return <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${map[color] || map.slate}`}>{label}</span>;
}

const EMPTY_FORM = {
  code: "", label: "", bonusAmount: "", currency: "NGN",
  maxRedemptions: "", totalBudget: "", startsAt: "", expiresAt: "",
  type: "general", assignedToUserId: null, assignedToUserName: "",
};

// ── User search picker — debounced search against GET /admin/users
// (the same endpoint UsersView.jsx already uses), so assigning a code
// to a user doesn't need a new backend lookup. ─────────────────────
function AssignedUserPicker({ userId, userName, onPick, onClear }) {
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await getAdminUsers({ search: query.trim(), limit: 8 });
        setResults(r?.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  if (userId) {
    return (
      <div className="flex items-center justify-between gap-2 bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5">
        <span className="text-white text-sm font-medium truncate">{userName || userId}</span>
        <button type="button" onClick={onClear} className="text-white/30 hover:text-white/60 text-xs flex-shrink-0">
          <i className="ri-close-circle-line" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Search by name or email…"
        className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
      {(searching || results.length > 0) && query.trim() && (
        <div className="absolute z-10 mt-1 w-full bg-[#1f1f1f] border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-48 overflow-y-auto">
          {searching && <div className="px-3 py-2 text-white/30 text-xs">Searching…</div>}
          {!searching && results.length === 0 && <div className="px-3 py-2 text-white/30 text-xs">No users found</div>}
          {!searching && results.map(u => (
            <button key={u.userId} type="button"
              onClick={() => { onPick(u); setQuery(""); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-white/5 flex flex-col">
              <span className="text-white text-xs font-medium">{u.userName}</span>
              <span className="text-white/30 text-[10px]">{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PromoCodeModal({ promo, onClose, onSave }) {
  const isNew = !promo?._id;
  const [form, setForm] = useState(promo ? {
    ...EMPTY_FORM,
    ...promo,
    startsAt:  promo.startsAt  ? new Date(promo.startsAt).toISOString().slice(0, 16)  : "",
    expiresAt: promo.expiresAt ? new Date(promo.expiresAt).toISOString().slice(0, 16) : "",
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.code || !form.bonusAmount) { setError("Code and bonus amount are required"); return; }
    if (form.type === "assigned" && !form.assignedToUserId) { setError("Pick a user to assign this code to"); return; }
    setSaving(true); setError("");
    try {
      await onSave({
        ...form,
        bonusAmount:    Number(form.bonusAmount),
        maxRedemptions: form.maxRedemptions !== "" ? Number(form.maxRedemptions) : null,
        totalBudget:    form.totalBudget    !== "" ? Number(form.totalBudget)    : null,
        startsAt:  form.startsAt  || null,
        expiresAt: form.expiresAt || null,
        assignedToUserId: form.type === "assigned" ? form.assignedToUserId : null,
      });
      onClose();
    } catch (err) { setError(err?.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">{isNew ? "Create Promo Code" : `Edit: ${promo.code}`}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Code</label>
              <input value={form.code} onChange={e => set("code", e.target.value.toUpperCase())}
                disabled={!isNew}
                placeholder="DAVIDO500" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white font-mono uppercase placeholder-white/20 focus:outline-none focus:border-[#00C896]/40 disabled:opacity-50" />
              {!isNew && <p className="text-white/20 text-[10px] mt-1">Code can't be changed after creation</p>}
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Label (celebrity/campaign)</label>
              <input value={form.label} onChange={e => set("label", e.target.value)}
                placeholder="Davido Q4 Campaign" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Type</label>
            <div className="flex gap-2">
              {[["general", "General"], ["assigned", "Assigned to user"]].map(([val, lbl]) => (
                <button key={val} type="button" disabled={!isNew}
                  onClick={() => set("type", val)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border transition-colors disabled:opacity-50 ${
                    form.type === val
                      ? "bg-[#00C896]/15 border-[#00C896]/40 text-[#00C896]"
                      : "bg-[#1a1a1a] border-white/10 text-white/40"
                  }`}>
                  {lbl}
                </button>
              ))}
            </div>
            {!isNew && <p className="text-white/20 text-[10px] mt-1">Type can't be changed after creation</p>}
            {form.type === "assigned" && (
              <div className="mt-2">
                {isNew ? (
                  <AssignedUserPicker
                    userId={form.assignedToUserId}
                    userName={form.assignedToUserName}
                    onPick={(u) => { set("assignedToUserId", u.userId); set("assignedToUserName", u.userName); if (!form.label) set("label", u.userName); }}
                    onClear={() => { set("assignedToUserId", null); set("assignedToUserName", ""); }}
                  />
                ) : (
                  <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm font-medium opacity-60">
                    {form.assignedToUserName || form.assignedToUserId}
                  </div>
                )}
                <p className="text-white/20 text-[10px] mt-1">
                  Redeeming this code also links the redeemer into this user's downline (deposit commissions), on top of the one-time bonus below.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Bonus Amount (₦)</label>
              <input type="number" value={form.bonusAmount} onChange={e => set("bonusAmount", e.target.value)}
                disabled={!isNew}
                placeholder="500" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40 disabled:opacity-50" />
              {!isNew && <p className="text-white/20 text-[10px] mt-1">Amount is fixed once redemptions may exist — disable and create a new code to change it</p>}
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Max Redemptions</label>
              <input type="number" value={form.maxRedemptions} onChange={e => set("maxRedemptions", e.target.value)}
                placeholder="unlimited" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Total Budget (₦)</label>
            <input type="number" value={form.totalBudget} onChange={e => set("totalBudget", e.target.value)}
              placeholder="unlimited — caps total payout for this code"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            <p className="text-white/20 text-[10px] mt-1">Stops new redemptions once this code's total payout would exceed the budget</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Starts At</label>
              <input type="datetime-local" value={form.startsAt} onChange={e => set("startsAt", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none [color-scheme:dark]" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Expires At</label>
              <input type="datetime-local" value={form.expiresAt} onChange={e => set("expiresAt", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none [color-scheme:dark]" />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create Code" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatsModal({ promo, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get(`/mtrade/admin/promo-codes/${promo._id}/stats`);
        if (!cancelled && r.data.status) setStats(r.data.data);
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [promo._id]);

  const byStatus = stats?.redemptions?.byStatus || { locked: 0, unlocked: 0, forfeited: 0 };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">{promo.code} — Redemptions</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="h-24 animate-pulse" />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#1a1a1a] rounded-xl px-3 py-3">
                  <p className="text-white font-bold text-xl">{stats?.redemptions?.total || 0}</p>
                  <p className="text-white/30 text-[10px]">Total redemptions</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-xl px-3 py-3">
                  <p className="text-white font-bold text-xl">₦{fmt(stats?.redemptions?.totalAmount)}</p>
                  <p className="text-white/30 text-[10px]">Total paid out</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/50 flex items-center gap-1.5"><i className="ri-lock-line text-amber-400" /> Locked (pending KYC)</span>
                  <span className="text-white font-semibold">{byStatus.locked || 0}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/50 flex items-center gap-1.5"><i className="ri-lock-unlock-line text-[#00C896]" /> Unlocked (withdrawable)</span>
                  <span className="text-white font-semibold">{byStatus.unlocked || 0}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/50 flex items-center gap-1.5"><i className="ri-close-circle-line text-red-400" /> Forfeited</span>
                  <span className="text-white font-semibold">{byStatus.forfeited || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PromoCodesView() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPromo, setEditPromo] = useState(null);
  const [statsPromo, setStatsPromo] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/mtrade/admin/promo-codes");
      if (r.data.status) setPromos(r.data.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPromos(); }, []);

  const handleSave = async (form) => {
    if (editPromo._id) {
      const r = await apiClient.patch(`/mtrade/admin/promo-codes/${editPromo._id}`, form);
      if (r.data.status) { fetchPromos(); showToast("Promo code updated"); }
    } else {
      const r = await apiClient.post("/mtrade/admin/promo-codes", form);
      if (r.data.status) { fetchPromos(); showToast("Promo code created"); }
    }
  };

  const handleToggle = async (p) => {
    await apiClient.patch(`/mtrade/admin/promo-codes/${p._id}`, { isActive: !p.isActive });
    setPromos(prev => prev.map(x => x._id === p._id ? { ...x, isActive: !x.isActive } : x));
  };

  const now = new Date();
  const totalRedeemed = promos.reduce((sum, p) => sum + (p.redeemedAmount || 0), 0);

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500/90 text-white" : "bg-[#00C896]/90 text-black"
        }`}>
          <i className={toast.type === "error" ? "ri-error-warning-line" : "ri-checkbox-circle-line"} />
          {toast.msg}
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Promo Codes</h2>
          <p className="text-white/40 text-xs mt-0.5">Celebrity & influencer signup bonus codes — bonus is credited immediately but locked until the user passes KYC</p>
        </div>
        <button onClick={() => setEditPromo({})}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896]/15 border border-[#00C896]/30 rounded-xl text-xs text-[#00C896] font-semibold hover:bg-[#00C896]/25">
          <i className="ri-add-line" /> New Promo Code
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Active Codes", value: promos.filter(p => p.isActive).length, icon: "ri-vip-crown-line", color: "#00C896" },
          { label: "Total Codes", value: promos.length, icon: "ri-coupon-3-line" },
          { label: "Total Paid Out", value: `₦${fmt(totalRedeemed)}`, icon: "ri-money-dollar-circle-line" },
        ].map(s => (
          <div key={s.label} className="bg-[#161616] border border-white/5 rounded-2xl px-4 py-3">
            <i className={`${s.icon} text-lg`} style={{ color: s.color || "rgba(255,255,255,0.2)" }} />
            <p className="text-white font-bold text-xl mt-1">{s.value}</p>
            <p className="text-white/30 text-[10px]">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Promo code list */}
      <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(4)].map((_, i) => <div key={i} className="px-4 py-4 h-16 animate-pulse" />)}
          </div>
        ) : promos.length === 0 ? (
          <div className="py-14 text-center">
            <i className="ri-vip-crown-line text-white/15 text-4xl mb-2 block" />
            <p className="text-white/25 text-sm">No promo codes yet</p>
          </div>
        ) : (
          promos.map(p => {
            const expired = p.expiresAt && new Date(p.expiresAt) < now;
            const redemptionsExhausted = p.maxRedemptions && p.redeemedCount >= p.maxRedemptions;
            const budgetExhausted = p.totalBudget && (p.redeemedAmount + p.bonusAmount) > p.totalBudget;
            const exhausted = redemptionsExhausted || budgetExhausted;
            return (
              <div key={p._id} className="flex items-center gap-3 px-4 py-3.5 border-b border-white/4 last:border-0 hover:bg-white/3 transition-colors group">
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${p.isActive && !expired && !exhausted ? "bg-[#00C896]/15" : "bg-white/5"}`}>
                  <i className={`ri-vip-crown-line text-base ${p.isActive && !expired && !exhausted ? "text-[#00C896]" : "text-white/25"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold text-sm font-mono">{p.code}</span>
                    {p.label && <span className="text-white/50 text-xs">{p.label}</span>}
                    <Badge label={`₦${fmt(p.bonusAmount)}`} color="emerald" />
                    {p.type === "assigned" && <Badge label={`→ ${p.assignedToUserName || "assigned user"}`} color="purple" />}
                    {expired              && <Badge label="Expired"   color="red" />}
                    {exhausted            && <Badge label="Exhausted" color="amber" />}
                    {!p.isActive          && <Badge label="Disabled"  color="slate" />}
                  </div>
                  <p className="text-white/30 text-xs mt-0.5">
                    {p.redeemedCount || 0}{p.maxRedemptions ? `/${p.maxRedemptions}` : ""} redemptions
                    {" · "}₦{fmt(p.redeemedAmount)}{p.totalBudget ? ` / ₦${fmt(p.totalBudget)}` : ""} paid out
                    {p.expiresAt && !expired && ` · expires ${new Date(p.expiresAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                  <button onClick={() => setStatsPromo(p)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center">
                    <i className="ri-bar-chart-box-line text-white/50 text-xs" />
                  </button>
                  <button onClick={() => setEditPromo(p)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center">
                    <i className="ri-edit-line text-white/50 text-xs" />
                  </button>
                  <button onClick={() => handleToggle(p)} className={`w-7 h-7 rounded-lg flex items-center justify-center ${p.isActive ? "bg-white/5 hover:bg-red-500/20" : "bg-[#00C896]/10 hover:bg-[#00C896]/20"}`}>
                    <i className={`text-xs ${p.isActive ? "ri-pause-line text-red-400/60" : "ri-play-line text-[#00C896]/60"}`} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {editPromo !== null && (
        <PromoCodeModal promo={editPromo} onClose={() => setEditPromo(null)} onSave={handleSave} />
      )}
      {statsPromo !== null && (
        <StatsModal promo={statsPromo} onClose={() => setStatsPromo(null)} />
      )}
    </div>
  );
}
